import { supabase } from './supabase';
import type { GlobalCustomer, OwnedCard } from '../types/database';

export async function getGlobalCustomers(): Promise<GlobalCustomer[]> {
  const { data, error } = await supabase
    .from('global_customers')
    .select('*')
    .order('name', { ascending: true });

  if (error) throw error;
  return (data || []).map(row => ({
    ...row,
    owned_cards: Array.isArray(row.owned_cards) ? row.owned_cards : [],
  }));
}

export async function createGlobalCustomer(
  name: string,
  ownedCards: OwnedCard[],
  email?: string
): Promise<GlobalCustomer> {
  const { data, error } = await supabase
    .from('global_customers')
    .insert({ name, owned_cards: ownedCards, email: email || null })
    .select()
    .single();

  if (error) throw error;
  return data;
}

export async function updateGlobalCustomer(
  id: string,
  name: string,
  ownedCards: OwnedCard[],
  email?: string
): Promise<GlobalCustomer> {
  const { data, error } = await supabase
    .from('global_customers')
    .update({ name, owned_cards: ownedCards, email: email || null, updated_at: new Date().toISOString() })
    .eq('id', id)
    .select()
    .single();

  if (error) throw error;
  return data;
}

export async function deleteGlobalCustomer(id: string): Promise<void> {
  const { error } = await supabase
    .from('global_customers')
    .delete()
    .eq('id', id);

  if (error) throw error;
}

let globalCustomerChannelId = 0;
export function subscribeToGlobalCustomers(callback: () => void) {
  return supabase
    .channel(`global_customers_changes_${++globalCustomerChannelId}`)
    .on(
      'postgres_changes',
      { event: '*', schema: 'public', table: 'global_customers' },
      callback
    )
    .subscribe();
}

export interface GlobalPaymentRecord {
  business_name: string;
  card_brand: string;
  charged_amount: number;
  month_year: string;
  payment_date: string;
}

export async function getAllPaymentsForGlobalCustomer(
  globalCustomerName: string
): Promise<GlobalPaymentRecord[]> {
  const { data: customers } = await supabase
    .from('xm_customers')
    .select('id, name, business_id')
    .eq('name', globalCustomerName);

  if (!customers || customers.length === 0) return [];

  const customerIds = customers.map(c => c.id);
  const businessIds = [...new Set(customers.map(c => c.business_id))];

  const { data: paymentsData, error } = await supabase
    .from('xm_customer_payments')
    .select('customer_id, card_brand, charged_amount, month_year, payment_date, business_id')
    .in('customer_id', customerIds)
    .order('payment_date', { ascending: false });

  if (error) throw error;

  const { data: businesses } = await supabase
    .from('businesses')
    .select('id, name')
    .in('id', businessIds);

  const bizMap = new Map((businesses || []).map(b => [b.id, b.name]));

  return (paymentsData || []).map(p => ({
    business_name: bizMap.get(p.business_id) || 'Unknown',
    card_brand: p.card_brand,
    charged_amount: Number(p.charged_amount),
    month_year: p.month_year,
    payment_date: p.payment_date,
  }));
}

export interface MonthlyUsageByCustomer {
  customerName: string;
  payments: GlobalPaymentRecord[];
}

export async function getMonthlyUsageForAllGlobalCustomers(
  monthYear: string
): Promise<Map<string, GlobalPaymentRecord[]>> {
  const { data: xmCustomers } = await supabase
    .from('xm_customers')
    .select('id, name, business_id')
    .eq('month_year', monthYear);

  if (!xmCustomers || xmCustomers.length === 0) return new Map();

  const customerIds = xmCustomers.map(c => c.id);
  const businessIds = [...new Set(xmCustomers.map(c => c.business_id))];

  const { data: paymentsData, error } = await supabase
    .from('xm_customer_payments')
    .select('customer_id, card_brand, charged_amount, month_year, payment_date, business_id')
    .in('customer_id', customerIds)
    .eq('month_year', monthYear)
    .order('payment_date', { ascending: false });

  if (error) throw error;

  const { data: businesses } = await supabase
    .from('businesses')
    .select('id, name')
    .in('id', businessIds);

  const bizMap = new Map((businesses || []).map(b => [b.id, b.name]));
  const custNameMap = new Map(xmCustomers.map(c => [c.id, c.name]));

  const result = new Map<string, GlobalPaymentRecord[]>();

  for (const p of (paymentsData || [])) {
    const custName = custNameMap.get(p.customer_id) || 'Unknown';
    const existing = result.get(custName) || [];
    existing.push({
      business_name: bizMap.get(p.business_id) || 'Unknown',
      card_brand: p.card_brand,
      charged_amount: Number(p.charged_amount),
      month_year: p.month_year,
      payment_date: p.payment_date,
    });
    result.set(custName, existing);
  }

  return result;
}

export function subscribeToXmPaymentsGlobal(callback: () => void) {
  return supabase
    .channel(`xm_payments_global_${++globalCustomerChannelId}`)
    .on(
      'postgres_changes',
      { event: '*', schema: 'public', table: 'xm_customer_payments' },
      callback
    )
    .subscribe();
}
